import { convexClient } from "../config/convex";
import { api } from "../../convex/_generated/api";
import type { Documento } from "./documentoService";
import { v4 as uuidv4 } from "uuid";

export async function obtenerDocumentoParaDescargar(
  id: string
): Promise<{ content: string; name: string; type: string } | null> {
  const result = await convexClient.query(api.documentos.getDownloadUrl, { id });
  return result;
}

function mapDoc(doc: {
  id: string;
  nombre: string;
  descripcion?: string;
  url: string;
  tipo: string;
  tamano: number;
  fecha_subida: string;
  user_id: string;
  nombre_usuario: string;
  eliminado?: boolean;
  fecha_eliminacion?: string;
  eliminado_por?: string;
  nombre_original?: string;
}): Documento {
  const downloadUrl = `/download-document/${doc.id}`;
  return {
    id: doc.id,
    nombre: doc.nombre ?? "",
    descripcion: doc.descripcion ?? "",
    url: downloadUrl,
    tipo: doc.tipo ?? "",
    tamaño: doc.tamano ?? 0,
    fechaSubida: new Date(doc.fecha_subida).toLocaleString(),
    userId: doc.user_id ?? "",
    nombreUsuario: doc.nombre_usuario ?? "",
    eliminado: doc.eliminado ?? false,
    fechaEliminacion: doc.fecha_eliminacion,
    eliminadoPor: doc.eliminado_por,
    nombreOriginal: doc.nombre_original ?? doc.nombre,
  };
}

export const documentoService = {
  async subirDocumento(
    archivo: File,
    nombre: string,
    descripcion: string,
    expedienteId?: string
  ): Promise<string> {
    const { userService } = await import("./userService.convex");
    const usuario = await userService.getCurrentUser();
    if (!usuario) throw new Error("No hay usuario autenticado");
    if (
      !archivo.type.includes("pdf") &&
      !archivo.type.includes("document") &&
      !archivo.type.includes("image/")
    ) {
      throw new Error("Solo se permiten archivos PDF, DOCX e imágenes");
    }
    if (archivo.size > 5 * 1024 * 1024) throw new Error("El tamaño máximo permitido es de 5MB");
    const id = uuidv4();

    // Subir archivo a Convex File Storage (soporta hasta 5MB)
    const postUrl = await convexClient.mutation(api.documentos.generateUploadUrl, {});
    const result = await fetch(postUrl, {
      method: "POST",
      headers: { "Content-Type": archivo.type },
      body: archivo,
    });
    if (!result.ok) {
      const err = await result.text();
      const msg = err || "Error al subir el archivo";
      const detail = `[${result.status} ${result.statusText}] ${msg}`;
      console.error("Error subida archivo:", { status: result.status, statusText: result.statusText, body: err });
      throw new Error(detail);
    }
    const { storageId } = await result.json();
    if (!storageId) throw new Error("No se recibió el ID del archivo subido");

    await convexClient.mutation(api.documentos.createWithStorage, {
      id,
      nombre: nombre || archivo.name,
      descripcion,
      storageId,
      tipo: archivo.type,
      tamano: archivo.size,
      fecha_subida: new Date().toISOString(),
      user_id: usuario.email,
      nombre_usuario: usuario.email,
      expediente_id: expedienteId,
      nombre_original: archivo.name,
    });
    if (expedienteId) {
      await convexClient.mutation(api.historialCambios.add, {
        expediente_id: expedienteId,
        fecha: new Date().toISOString(),
        usuario: usuario.email,
        cambio: `Documento subido: ${nombre || archivo.name}`,
      });
    }
    return id;
  },

  async obtenerDocumentos(): Promise<Documento[]> {
    const docs = await convexClient.query(api.documentos.list, {});
    return docs.map(mapDoc);
  },

  async eliminarDocumento(documentoId: string): Promise<void> {
    const { userService } = await import("./userService.convex");
    const usuario = await userService.getCurrentUser();
    if (!usuario) throw new Error("No hay usuario autenticado");
    const doc = await convexClient.query(api.documentos.getById, { id: documentoId });
    if (!doc) throw new Error("Documento no encontrado o ya eliminado");
    const esAdmin = await userService.isAdmin();
    if (doc.user_id !== usuario.email && !esAdmin)
      throw new Error("No tienes permisos para eliminar este documento");
    await convexClient.mutation(api.documentos.markDeleted, {
      id: documentoId,
      fecha_eliminacion: new Date().toISOString(),
      eliminado_por: usuario.email,
    });
  },

  async obtenerDocumentosPorUsuario(userId: string): Promise<Documento[]> {
    const docs = await convexClient.query(api.documentos.listByUserId, { user_id: userId });
    return docs.map(mapDoc);
  },

  async obtenerDocumentosPorExpediente(expedienteId: string): Promise<Documento[]> {
    const docs = await convexClient.query(api.documentos.listByExpedienteId, {
      expediente_id: expedienteId,
    });
    return docs.map(mapDoc);
  },

  obtenerDocumentoParaDescargar,
};
